import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, tap, timeout, zip } from 'rxjs';

import { EcmDispatcherModule } from '@ecm/dispatcher';

import { Transaction } from '../enums/transaction';
import { BcoAbstractService } from './bco-abstract.service';
import { BcoCsvFormatter } from './bco-csv.formatter';

@Injectable({
    providedIn: 'root',
})
export class BcoCsvService extends BcoAbstractService {
    /**
     * @inheritDoc
     */
    protected readonly CLASS_NAME = 'BcoCsvService';

    /**
     * @inheritDoc
     */
    protected readonly BCO_ENDPOINT = 'stat.bco';

    /**
     * Header used for BCO logs storage
     * @type {string}
     */
    private readonly TRACKING_FLOW_HEADER_NAME: string = 'X-ORANGE-Tracking-flow';

    public constructor(
        private httpClient: HttpClient,
        private bcoCsvFormatter: BcoCsvFormatter,
        @Inject(LOCALE_ID) protected override locale: string,
    ) {
        super(locale);
    }

    /**
     * @override
     * @inheritDoc
     * - Logs are sent to an endpoint in a HTTP GET request header;
     * - Logs are sent by packets in CSV format;
     * - Each log in a packet is separated by a "pipe" char;
     */
    public flushLogs(): Observable<boolean> {
        this.reinjectFailedLogsInList();
        const requests: Array<Observable<any>> = [of('publish something to have a next even without request')];

        while (this.logsQueue.length > 0) {
            const logsPacket: Transaction[] = this.logsQueue.splice(0, this.BCO_PACKET_SIZE);
            const computedLog: string = logsPacket
                .map((log: Transaction) => this.bcoCsvFormatter.format(log))
                .join('|');

            const headers: HttpHeaders = new HttpHeaders()
                .set(this.TRACKING_FLOW_HEADER_NAME, computedLog)
                .set('Cache-Control', 'no-cache, no-store');
            const requestOptions = {
                headers,
                withCredentials: true,
                reportProgress: false,
                responseType: 'text' as const,
            };

            EcmDispatcherModule.sendConsoleInfo(
                this.CLASS_NAME,
                'Sending ' + logsPacket.length + ' BCO logs to Apache (async)',
            );

            requests.push(
                this.httpClient.get(this.BCO_ENDPOINT, requestOptions).pipe(
                    tap(() => {
                        EcmDispatcherModule.sendConsoleInfo(this.CLASS_NAME, logsPacket.length + ' BCO logs well sent');
                    }),
                    timeout(this.BCO_TIMEOUT),
                    catchError((error) => {
                        // Reinject failed log in queue on network error only
                        if (error.error instanceof ErrorEvent || error.error?.type === 'error') {
                            this.failedLogsQueue.push(...logsPacket);
                            EcmDispatcherModule.sendConsoleError(
                                this.CLASS_NAME,
                                'Network Error, ' + logsPacket.length + ' BCO logs re-added to the queue',
                            );
                        } else {
                            EcmDispatcherModule.sendConsoleError(
                                this.CLASS_NAME,
                                'HTTP Error received but assume than the ' + logsPacket.length + ' BCO logs are sent',
                            );
                        }
                        return of("Error on one request but don't stop other requests");
                    }),
                ),
            );
        }

        const zippedResult = zip(...requests).pipe(
            map(() => true),
            shareReplay(),
        );

        zippedResult.subscribe();

        return zippedResult;
    }
}
